import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight, Landmark, MapPin, Phone, ExternalLink } from "lucide-react";
import Layout from "@/components/site/Layout";
import PageHero from "@/components/site/PageHero";

const NEEDED = [
  "The deceased owned a home or other real estate solely in their name.",
  "The property was not held in a living trust.",
  "There was no designated beneficiary or joint owner with rights of survivorship.",
  "The estate exceeds California's probate threshold.",
  "Family members cannot agree on how the estate should be handled.",
];

const NOT_NEEDED = [
  "The inherited property is held in a revocable living trust.",
  "The home is owned jointly with rights of survivorship.",
  "The property is community property with right of survivorship.",
  "The asset passes directly to a named beneficiary.",
  "The estate qualifies for California's simplified procedures for smaller estates.",
];

const HELP = [
  "A complimentary Probate Comparative Market Analysis (PCMA)",
  "Recommendations for repairs, cleanout, and estate sale services",
  "Coordination with your probate attorney",
  "Marketing the probate property to maximize its value",
  "Managing the sale from listing through closing",
];

export default function WhenProbateNeeded() {
  return (
    <Layout>
      <PageHero
        breadcrumb="Home / Probates / When Is Probate Needed?"
        overline="One of the first questions families ask"
        title="When Is Probate"
        accentWord="Needed?"
        description={'After someone passes away, one of the first questions families often ask is, "Do we need to go through probate?" The answer depends on how the property was owned, whether there is a valid trust, and the total value of the estate.'}
        image="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=1600&q=80"
        rightImage="https://images.pexels.com/photos/5668772/pexels-photo-5668772.jpeg?auto=compress&cs=tinysrgb&w=1200"
        rightImageAlt="Family reviewing probate paperwork with an advisor"
      />

      {/* Intro */}
      <section className="py-16 bg-white" data-testid="wpn-intro">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-navy-700 leading-relaxed">
            In California, probate is generally required when a person dies owning assets in their name alone and the estate exceeds the state&apos;s probate threshold. Probate is the court-supervised process of transferring ownership of assets, paying debts, and distributing property to heirs or beneficiaries.
          </p>
        </div>
      </section>

      {/* Two-column: Needed vs Not Needed */}
      <section className="py-16 bg-navy-50/40" data-testid="wpn-compare">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-navy-100 bg-white p-8"
            data-testid="wpn-needed"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold">
              <CheckCircle2 size={14} className="text-gold-400" />
              Probate May Be Needed If
            </div>
            <ul className="mt-6 space-y-3">
              {NEEDED.map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`needed-${i}`}>
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gold-500" />
                  <span className="text-base text-navy-700 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="rounded-3xl border border-gold-500/30 bg-gold-500/5 p-8"
            data-testid="wpn-not-needed"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-gold-500 text-navy-900 px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold">
              <XCircle size={14} />
              Probate May Not Be Needed If
            </div>
            <ul className="mt-6 space-y-3">
              {NOT_NEEDED.map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`not-needed-${i}`}>
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-navy-900" />
                  <span className="text-base text-navy-700 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-sm text-navy-600 italic border-t border-gold-500/30 pt-4">
              Every estate is different, so it&apos;s important to have the situation reviewed by a qualified probate attorney.
            </div>
          </motion.div>
        </div>
      </section>

      {/* Estate includes real estate */}
      <section className="py-20 bg-white" data-testid="wpn-real-estate">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <span className="section-overline">If The Estate Includes Real Estate</span>
          <h2 className="mt-3 font-heading text-4xl text-navy-900 leading-tight">
            An inherited home in probate <em className="italic text-gold-600">can still be sold</em>.
          </h2>
          <p className="mt-5 text-lg text-navy-700 leading-relaxed">
            If an inherited home must go through probate, it does <em>not</em> mean it cannot be sold. In many cases, the personal representative or executor can sell the property during probate, although court approval may be required depending on the circumstances and the authority granted by the court.
          </p>
          <p className="mt-4 text-lg text-navy-700 leading-relaxed">
            Selling an inherited property often involves preparing the home for sale, determining its market value, coordinating with the probate attorney, and complying with California probate requirements.
          </p>
        </div>
      </section>

      {/* How We Can Help */}
      <section className="py-20 bg-navy-50/40" data-testid="wpn-help">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="section-overline">How We Can Help</span>
            <h2 className="mt-3 font-heading text-4xl text-navy-900 leading-tight">
              A Probate Real Estate Specialist by <em className="italic text-gold-600">your side</em>.
            </h2>
            <p className="mt-4 text-lg text-navy-600 leading-relaxed">
              Selling a probate property involves more than simply listing a home. As a Probate Real Estate Specialist serving Orange County and surrounding communities, I work with executors, administrators, trustees, heirs, and probate attorneys to help make the sale as smooth as possible.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {HELP.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`help-${i}`}
                className="flex items-start gap-4 rounded-2xl border border-navy-100 bg-white p-5 hover:border-gold-400 hover:shadow-md transition-all"
              >
                <div className="h-10 w-10 shrink-0 rounded-xl bg-navy-900 text-gold-400 flex items-center justify-center font-heading">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-base text-navy-700 leading-relaxed pt-1">{h}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Next step */}
      <section className="py-20 bg-white" data-testid="wpn-next-step">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-navy-900 text-white p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-gold-500/15 blur-3xl" />
            <div className="relative">
              <span className="section-overline text-gold-400">What Is The Next Step?</span>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl leading-tight max-w-3xl">
                Received the Court Order and your Letters? <em className="italic text-gold-400">Let's talk.</em>
              </h2>
              <p className="mt-5 text-white/70 max-w-3xl">
                If you have already received the Court Order and the Letters Testamentary or Letters of Administration, contact Probate Solution OC now for a Probate Comparative Market Analysis (PCMA) and a one-on-one consultation of your property — all at <span className="font-semibold text-gold-400">NO cost to you</span>.
              </p>
              <p className="mt-4 text-white/70 max-w-3xl">
                If you have not received the Court Order nor the Letters, let us know if there is anything we can help you with.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="wpn-cta-contact" className="btn-gold group">
                  Request Your Free PCMA
                  <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Link>
                <a href="tel:+17143913645" data-testid="wpn-cta-phone" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-medium hover:bg-white hover:text-navy-900 transition-all">
                  <Phone size={14} />
                  (714) 391-3645
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Court reference */}
      <section className="py-14 bg-navy-50/40" data-testid="wpn-court-info">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-navy-100 bg-white p-6 flex flex-col md:flex-row md:items-center gap-6">
            <div className="h-14 w-14 rounded-xl bg-gold-500/10 text-gold-600 flex items-center justify-center shrink-0">
              <Landmark size={22} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.24em] text-navy-500 font-semibold">Official Resource</div>
              <div className="mt-1 font-heading text-lg text-navy-900">Superior Court of California · County of Orange</div>
              <div className="mt-1 text-sm text-navy-600 flex items-center gap-1.5">
                <MapPin size={12} className="text-gold-600" />
                700 Civic Center Drive West, Santa Ana, CA 92801
              </div>
            </div>
            <a
              href="https://www.occourts.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wpn-occourts-link"
              className="inline-flex items-center gap-2 rounded-full border border-navy-800 text-navy-800 px-5 py-2.5 text-sm font-medium hover:bg-navy-800 hover:text-white transition-all"
            >
              www.occourts.org
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
